/**
 * SchedulesCard — the per-client Schedules tab (Story 7.2).
 *
 * Lists a client's automated report schedules (frequency label, next-run
 * formatted in the schedule's tz via built-in Intl, template name or "—",
 * active badge, recipient count). A "Schedule Report" button + per-row
 * edit/delete controls are gated by usePermission().hasPermission(
 * Actions.MANAGE_REPORTS) — owner/admin only; viewers see the list read-only.
 *
 * Edit opens the centered ScheduleFormDialog pre-filled; delete uses a §4.4
 * confirmation Dialog. Both dialogs are ALWAYS mounted (§4.6 — never
 * conditionally unmount; the edit/delete targets linger via a last-state copy
 * so the exit animation plays). Card chrome per §7 (rounded-[22px], 0.5px
 * inline border, shadow-xs).
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@provenleap/ui";
import {
  CalendarBlank,
  CalendarPlus,
  PencilSimple,
  Trash,
  Plus,
  ClockCounterClockwise,
  Pause,
  Play,
} from "@phosphor-icons/react";
import {
  listSchedules,
  deleteSchedule,
  updateSchedule,
  type Frequency,
  type ScheduleProjection,
} from "../../lib/schedulesApi";
import { usePermission, Actions } from "../../hooks/usePermission";
import { ScheduleFormDialog, formatNextRun } from "./ScheduleFormDialog";
import { ScheduleHistoryDialog } from "./ScheduleHistoryDialog";
import type { ToastType } from "../../hooks/useToast";

interface SchedulesCardProps {
  clientId: string;
  /** The client's timezone — passed through to default the dialog's picker. */
  clientTimezone?: string | null;
  showToast: (type: ToastType, message: string) => void;
}

const CARD_BORDER = "0.5px solid var(--hairline)";

const FREQUENCY_LABELS: Record<Frequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
};

function ScheduleRow({
  schedule,
  canManage,
  onHistory,
  onPauseResume,
  onEdit,
  onDelete,
}: {
  schedule: ScheduleProjection;
  canManage: boolean;
  onHistory: () => void;
  onPauseResume: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const isActive = schedule.isActive;
  return (
    <div
      className="flex items-center justify-between gap-3 py-3"
      data-testid="schedule-row"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {FREQUENCY_LABELS[schedule.frequency]}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
              schedule.isActive
                ? "bg-green-500/10 text-green-700"
                : "bg-muted text-muted-foreground"
            }`}
            data-testid="schedule-active-badge"
          >
            {schedule.isActive ? "Active" : "Paused"}
          </span>
        </div>
        <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
          {schedule.templateName ?? "—"}
        </p>
        <p className="mt-0.5 text-[12px] text-muted-foreground/80">
          Next: {formatNextRun(schedule.nextRunAt, schedule.timezone)} ·{" "}
          {schedule.recipientCount}{" "}
          {schedule.recipientCount === 1 ? "recipient" : "recipients"}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* History is viewer-readable — shown to everyone. */}
        <button
          type="button"
          aria-label="View delivery history"
          onClick={onHistory}
          className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
          data-testid="schedule-history-button"
        >
          <ClockCounterClockwise size={14} />
        </button>
        {canManage && (
          <>
            <button
              type="button"
              aria-label={isActive ? "Pause schedule" : "Resume schedule"}
              title={isActive ? "Pause" : "Resume"}
              onClick={onPauseResume}
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
              data-testid="schedule-pause-resume-button"
            >
              {isActive ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button
              type="button"
              aria-label="Edit schedule"
              onClick={onEdit}
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
            >
              <PencilSimple size={14} />
            </button>
            <button
              type="button"
              aria-label="Delete schedule"
              onClick={onDelete}
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive transition-colors cursor-pointer"
            >
              <Trash size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function SchedulesCard({
  clientId,
  clientTimezone,
  showToast,
}: SchedulesCardProps) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Actions.MANAGE_REPORTS);
  const queryClient = useQueryClient();

  const { data: schedules, isLoading } = useQuery({
    queryKey: ["schedules", clientId],
    queryFn: () => listSchedules(clientId),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editSchedule, setEditSchedule] = useState<ScheduleProjection | null>(
    null
  );
  const [lastEditSchedule, setLastEditSchedule] =
    useState<ScheduleProjection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScheduleProjection | null>(
    null
  );
  const [lastDeleteTarget, setLastDeleteTarget] =
    useState<ScheduleProjection | null>(null);
  // Story 7.9 — delivery-history dialog target (linger so the exit animation plays).
  const [historyTarget, setHistoryTarget] = useState<ScheduleProjection | null>(
    null
  );
  const [lastHistoryTarget, setLastHistoryTarget] =
    useState<ScheduleProjection | null>(null);
  // Story 7.12 — pause/resume confirmation target (linger so the exit animation plays).
  const [pauseResumeTarget, setPauseResumeTarget] =
    useState<ScheduleProjection | null>(null);
  const [lastPauseResumeTarget, setLastPauseResumeTarget] =
    useState<ScheduleProjection | null>(null);

  // Keep the last non-null target alive ~300ms so the dialog exit animation can
  // play (§4.6 — never conditionally unmount the modal for a non-null prop).
  useEffect(() => {
    if (editSchedule) {
      setLastEditSchedule(editSchedule);
      return;
    }
    if (!lastEditSchedule) return;
    const t = setTimeout(() => setLastEditSchedule(null), 300);
    return () => clearTimeout(t);
  }, [editSchedule, lastEditSchedule]);

  useEffect(() => {
    if (deleteTarget) {
      setLastDeleteTarget(deleteTarget);
      return;
    }
    if (!lastDeleteTarget) return;
    const t = setTimeout(() => setLastDeleteTarget(null), 300);
    return () => clearTimeout(t);
  }, [deleteTarget, lastDeleteTarget]);

  useEffect(() => {
    if (historyTarget) {
      setLastHistoryTarget(historyTarget);
      return;
    }
    if (!lastHistoryTarget) return;
    const t = setTimeout(() => setLastHistoryTarget(null), 300);
    return () => clearTimeout(t);
  }, [historyTarget, lastHistoryTarget]);

  useEffect(() => {
    if (pauseResumeTarget) {
      setLastPauseResumeTarget(pauseResumeTarget);
      return;
    }
    if (!lastPauseResumeTarget) return;
    const t = setTimeout(() => setLastPauseResumeTarget(null), 300);
    return () => clearTimeout(t);
  }, [pauseResumeTarget, lastPauseResumeTarget]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules", clientId] });
      showToast("success", "Schedule deleted");
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      showToast("error", err.message ?? "Failed to delete schedule");
    },
  });

  // Story 7.12 — pause/resume toggles isActive via the existing updateSchedule
  // PATCH (no new endpoint). The server recomputes next_run_at on resume.
  const pauseResumeMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateSchedule(id, { isActive }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["schedules", clientId] });
      showToast(
        "success",
        variables.isActive ? "Schedule resumed" : "Schedule paused"
      );
      setPauseResumeTarget(null);
    },
    onError: (err: Error) => {
      showToast("error", err.message ?? "Failed to update schedule");
    },
  });

  const hasSchedules = Boolean(schedules && schedules.length > 0);

  return (
    <div
      className="!rounded-[22px] bg-background p-5 shadow-xs"
      style={{ border: CARD_BORDER }}
      data-testid="schedules-card"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarBlank size={18} className="text-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Schedules</h3>
        </div>
        {canManage && (
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: CARD_BORDER }}
            onClick={() => setCreateOpen(true)}
            data-testid="schedule-report-button"
          >
            <Plus size={16} className="mr-1.5" />
            Schedule Report
          </Button>
        )}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-4" data-testid="schedules-loading">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : !hasSchedules ? (
          <div
            className="flex flex-col items-center justify-center py-10 text-center"
            data-testid="schedules-empty"
          >
            <div className="mb-3 rounded-full bg-muted p-3">
              <CalendarPlus size={28} className="text-muted-foreground" />
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              No schedules yet — automate report delivery on a recurring cadence
            </p>
            {canManage && (
              <Button
                variant="outline"
                className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                style={{ border: CARD_BORDER }}
                onClick={() => setCreateOpen(true)}
                data-testid="schedule-report-empty-button"
              >
                <Plus size={16} className="mr-1.5" />
                Schedule Report
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y-[0.5px] divide-input/70">
            <AnimatePresence initial={false}>
              {schedules!.map((schedule, index) => (
                <motion.div
                  key={schedule.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.03 * (index + 1) }}
                >
                  <ScheduleRow
                    schedule={schedule}
                    canManage={canManage}
                    onHistory={() => setHistoryTarget(schedule)}
                    onPauseResume={() => setPauseResumeTarget(schedule)}
                    onEdit={() => setEditSchedule(schedule)}
                    onDelete={() => setDeleteTarget(schedule)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Create — always-mounted (§4.6). */}
      <ScheduleFormDialog
        clientId={clientId}
        clientTimezone={clientTimezone}
        open={createOpen}
        onOpenChange={setCreateOpen}
        showToast={showToast}
      />

      {/* Edit — lingers via lastEditSchedule so the exit animation plays. */}
      {lastEditSchedule && (
        <ScheduleFormDialog
          clientId={clientId}
          clientTimezone={clientTimezone}
          open={!!editSchedule}
          onOpenChange={(o) => !o && setEditSchedule(null)}
          schedule={lastEditSchedule}
          showToast={showToast}
        />
      )}

      {/* Story 7.9 — delivery history (viewer-readable) — lingers via lastHistoryTarget. */}
      {lastHistoryTarget && (
        <ScheduleHistoryDialog
          // key by schedule → a fresh instance per schedule (resets the paging
          // pageSize; with keepPreviousData this prevents one schedule's history
          // from flashing into another's on a reopen-within-the-linger-window).
          key={lastHistoryTarget.id}
          scheduleId={lastHistoryTarget.id}
          trackingEnabled={lastHistoryTarget.trackingEnabled}
          open={!!historyTarget}
          onOpenChange={(o) => !o && setHistoryTarget(null)}
          showToast={showToast}
        />
      )}

      {/* Delete confirmation (§4.4) — lingers via lastDeleteTarget. */}
      {lastDeleteTarget && (
        <DeleteScheduleDialog
          open={!!deleteTarget}
          frequencyLabel={FREQUENCY_LABELS[lastDeleteTarget.frequency]}
          templateName={lastDeleteTarget.templateName}
          pending={deleteMutation.isPending}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          onConfirm={() => {
            if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
          }}
        />
      )}

      {/* Story 7.12 — pause/resume confirmation (§4.4) — lingers via lastPauseResumeTarget. */}
      {lastPauseResumeTarget && (
        <PauseResumeScheduleDialog
          open={!!pauseResumeTarget}
          isActive={lastPauseResumeTarget.isActive}
          frequencyLabel={FREQUENCY_LABELS[lastPauseResumeTarget.frequency]}
          templateName={lastPauseResumeTarget.templateName}
          pending={pauseResumeMutation.isPending}
          onOpenChange={(o) => !o && setPauseResumeTarget(null)}
          onConfirm={() => {
            if (pauseResumeTarget)
              pauseResumeMutation.mutate({
                id: pauseResumeTarget.id,
                isActive: !pauseResumeTarget.isActive,
              });
          }}
        />
      )}
    </div>
  );
}

function DeleteScheduleDialog({
  open,
  frequencyLabel,
  templateName,
  pending,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  frequencyLabel: string;
  templateName: string | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  function handleOpenChange(next: boolean) {
    if (!next && pending) return;
    onOpenChange(next);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete schedule?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    The {frequencyLabel.toLowerCase()} delivery
                    {templateName ? ` of "${templateName}"` : ""} will stop. This
                    cannot be undone.
                  </DialogPrimitive.Description>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: CARD_BORDER }}
                    onClick={() => handleOpenChange(false)}
                    disabled={pending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    disabled={pending}
                    onClick={onConfirm}
                    data-testid="confirm-delete-schedule"
                  >
                    {pending ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

/**
 * Story 7.12 — pause/resume confirmation (§4.4). A brief centered modal: pausing
 * stops upcoming deliveries; resuming reschedules from now (the server recomputes
 * next_run_at on the false→true transition). Non-destructive, so the confirm
 * button uses the default variant (not `destructive`).
 */
function PauseResumeScheduleDialog({
  open,
  isActive,
  frequencyLabel,
  templateName,
  pending,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  /** The CURRENT active state — true ⇒ the action is "Pause", false ⇒ "Resume". */
  isActive: boolean;
  frequencyLabel: string;
  templateName: string | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  function handleOpenChange(next: boolean) {
    if (!next && pending) return;
    onOpenChange(next);
  }

  const actionVerb = isActive ? "Pause" : "Resume";
  const ofTemplate = templateName ? ` of "${templateName}"` : "";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {actionVerb} schedule?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {isActive
                      ? `The ${frequencyLabel.toLowerCase()} delivery${ofTemplate} will stop sending until you resume it.`
                      : `The ${frequencyLabel.toLowerCase()} delivery${ofTemplate} will resume — the next run is rescheduled from now.`}
                  </DialogPrimitive.Description>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: CARD_BORDER }}
                    onClick={() => handleOpenChange(false)}
                    disabled={pending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    disabled={pending}
                    onClick={onConfirm}
                    data-testid="confirm-pause-resume-schedule"
                  >
                    {pending
                      ? isActive
                        ? "Pausing…"
                        : "Resuming…"
                      : actionVerb}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
